import type { EventListQuery } from "@cdt/shared";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import {
  parseSearchParams,
  queryToSearchParams,
} from "@/lib/eventsQueryParams";

type QueryPatch = Partial<EventListQuery>;

const PAGE_ONLY_KEYS = new Set<keyof EventListQuery>([
  "page",
  "pageSize",
  "sortBy",
  "sortOrder",
]);

function patchTouchesFilters(patch: QueryPatch): boolean {
  return Object.keys(patch).some(
    (key) => !PAGE_ONLY_KEYS.has(key as keyof EventListQuery),
  );
}

export interface UseEventsFiltersResult {
  query: EventListQuery;
  setPatch: (patch: QueryPatch) => void;
  clearAll: () => void;
}

/**
 * URL-bound filter state for the events list. Any patch that changes a
 * filter (status, dateFrom, dateTo, search) resets page to 1, while
 * pagination/sorting patches preserve the current page.
 */
export function useEventsFilters(): UseEventsFiltersResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const query = useMemo(() => parseSearchParams(searchParams), [searchParams]);

  const setPatch = useCallback(
    (patch: QueryPatch) => {
      const next: EventListQuery = {
        ...query,
        ...patch,
        ...(patchTouchesFilters(patch) && patch.page === undefined
          ? { page: 1 }
          : {}),
      };
      setSearchParams(queryToSearchParams(next), { replace: false });
    },
    [query, setSearchParams],
  );

  const clearAll = useCallback(() => {
    setSearchParams(
      queryToSearchParams({
        ...query,
        status: undefined,
        dateFrom: undefined,
        dateTo: undefined,
        search: undefined,
        priority: undefined,
        priorityMin: undefined,
        page: 1,
      }),
      { replace: false },
    );
  }, [query, setSearchParams]);

  return { query, setPatch, clearAll };
}
